// Geometry-only helpers for the power-plant overlay. Kept free of React,
// MapLibre and HTTP so marker placement stays deterministic and testable.

const normalise = (value) => String(value || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()

/** Select the hand-drawn icon family for a plant's displayed type. */
export const powerPlantIconKey = (type) => {
  const name = normalise(type)
  if (name.includes('nucleaire')) return 'nuclear'
  if (name.includes('solaire')) return 'solar'
  if (name.includes('eolien')) return 'wind'
  if (name.includes('hydro')) return 'hydro'
  return 'thermal'
}

const coordinatePairs = (geometry) => {
  if (!geometry?.coordinates) return []
  const out = []
  const visit = (node) => {
    if (Array.isArray(node?.[0]) || Array.isArray(node?.[1])) node.forEach(visit)
    else if (Number.isFinite(node?.[0]) && Number.isFinite(node?.[1])) out.push(node)
  }
  visit(geometry.coordinates)
  return out
}

const regionCentres = (regionsData) => new Map((regionsData?.features || [])
  .map((feature) => {
    const p = feature.properties || {}
    if (p.region_id == null) return null
    if (Array.isArray(p._centroid)) return [String(p.region_id), p._centroid]
    const pairs = coordinatePairs(feature.geometry)
    if (!pairs.length) return null
    const sum = pairs.reduce((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0])
    return [String(p.region_id), [sum[0] / pairs.length, sum[1] / pairs.length]]
  })
  .filter(Boolean))

/**
 * Place plants at their persisted world position. Pre-map records have no
 * coordinates, so they retain a stable region-centre fallback with a small
 * radial offset that keeps multiple legacy rows clickable.
 */
export const powerPlantsToGeoJSON = (plants, regionsData) => {
  const centres = regionCentres(regionsData)
  const byRegion = new Map()
  ;(plants || []).forEach((plant) => {
    const key = String(plant.region_id)
    if (!centres.has(key)) return
    if (!byRegion.has(key)) byRegion.set(key, [])
    byRegion.get(key).push(plant)
  })

  const features = []
  byRegion.forEach((group, regionId) => {
    const [lng, lat] = centres.get(regionId)
    group.sort((a, b) => String(a.id).localeCompare(String(b.id))).forEach((plant, index) => {
      const angle = group.length > 1 ? (Math.PI * 2 * index) / group.length : 0
      const radius = group.length > 1 ? 0.018 : 0
      const hasPosition = Number.isFinite(plant.world_x) && Number.isFinite(plant.world_y)
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: hasPosition
            ? [plant.world_x, plant.world_y]
            : [lng + Math.cos(angle) * radius, lat + Math.sin(angle) * radius],
        },
        properties: {
          id: plant.id,
          region_id: plant.region_id,
          region_name: plant.region_name,
          type: plant.type,
          level: plant.level,
          quantity: plant.quantity || 1,
          production_mwh: plant.production_mwh || 0,
          is_operational: plant.is_operational !== false,
          construction_end_date: plant.construction_end_date || null,
          world_x: plant.world_x ?? null,
          world_y: plant.world_y ?? null,
          icon_key: powerPlantIconKey(plant.type),
        },
      })
    })
  })
  return { type: 'FeatureCollection', features }
}
